import _ from 'lodash';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Divider, Typography, VStack } from '@vivaahealth/design-system';

import * as store from '../../../reducers/store';
import * as utils from '../../../utils';

import { makeFetchInsuranceCards } from '../../../reducers/patient';
import { MONTH_DAY_YEAR_FORMAT } from '../../../utils/dateTime';
import { InsuranceCard } from '../../../reducers/store';

import Row from '../../../components/Row';
import TextData from '../../../components/TextData';

interface InsuranceProps {
  field: store.FormFieldWithConfig;
}

const StyledLogo = styled.img`
  width: 32px;
  object-fit: contain;
  object-position: left;
`;

const StyledTypography = styled(Typography)`
  text-align: center;
`;

const Insurance = ({ field }: InsuranceProps) => {
  const { t } = useTranslation('review');
  const dispatch = useDispatch();
  const cards =
    useSelector((state: store.Loaded) => state.insuranceCards).data || [];

  useEffect(() => {
    makeFetchInsuranceCards(dispatch)(field.previsitFormSetId);
  }, [field.previsitFormSetId, dispatch]);

  if (!cards.length) {
    return (
      <StyledTypography variant={'paragraph-medium'} color="secondary">
        {t('noInsuranceMessage')}
      </StyledTypography>
    );
  }

  return (
    <VStack gap={16}>
      {cards.map((card, index) => (
        <React.Fragment key={card.id}>
          <Card card={card} />
          {index !== cards.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </VStack>
  );
};

const Card = ({ card }: { card: InsuranceCard }) => {
  const { t } = useTranslation('review');
  const insuranceProviderImgSrc =
    card.logoUrl && utils.getPrevisitAsset(card.logoUrl);
  const insuranceProvider =
    card.payorDisplayName ||
    card.planName ||
    card.nameRaw ||
    t('insuranceCardNamePlaceHolder');
  const memberId = card.memberId || card.primaryPolicyHolderMemberId;
  const providerNameComponent = insuranceProviderImgSrc ? (
    <StyledLogo
      src={insuranceProviderImgSrc}
      alt={insuranceProvider || 'logo'}
    />
  ) : (
    <Typography variant="title-medium" bold>
      {insuranceProvider}
    </Typography>
  );
  const cardsInformation = [
    {
      label: t('policyHolderLabel'),
      value:
        card.primaryPolicyHolderFirstName && card.primaryPolicyHolderLastName
          ? [
              card.primaryPolicyHolderFirstName,
              card.primaryPolicyHolderLastName,
            ].join(' ')
          : '–',
    },
    {
      label: t('memberIdLabel'),
      value: memberId || '–',
    },
    {
      label: t('groupNumberLabel'),
      value: card.groupNumber || '–',
    },
    {
      label: t('coverageStartDateLabel'),
      value: card.coverageStartDate
        ? moment(card.coverageStartDate).format(MONTH_DAY_YEAR_FORMAT)
        : '–',
    },
  ];
  return (
    <VStack gap={16}>
      {providerNameComponent}
      {_.chunk(cardsInformation, 2).map((chunk, index) => (
        <Row hideIconContainer key={index}>
          {chunk.map((cardInfo, index) => (
            <TextData
              key={cardInfo.label}
              label={cardInfo.label}
              value={cardInfo.value}
            />
          ))}
        </Row>
      ))}
    </VStack>
  );
};

export default Insurance;
